import { useCallback, useEffect, useState } from 'preact/hooks';
import { Avatar } from '../components/Avatar.js';
import { Recordings } from '../components/Recordings.js';
import { haptic } from '../lib/haptics.js';
import { primeMicrophone } from '../lib/media.js';
import * as api from '../lib/api.js';
import * as auth from '../lib/auth.js';
import type { HostIdentity } from '../lib/api.js';

/**
 * The host's home screen — the whole app, for the two people who run the show.
 *
 * One button. Tapping it creates today's room if nobody has yet, joins it if
 * someone has, and puts the caller on stage as a moderator either way. There is
 * no room code to remember, no console, and no operator key: whichever host
 * opens the app first makes the room, the second one walks into it.
 *
 * The name is asked for once and kept locally. It is not identity — the session
 * cookie is — it is just what the other person sees.
 */
const NAME_KEY = 'waves.host.name';

export function HostHome({
  identity,
  onLive,
}: {
  identity: HostIdentity;
  /** Receives the grant AND the live microphone opened under the tap. */
  onLive: (
    grant: Awaited<ReturnType<typeof api.startTodaysShow>>,
    micTrack: MediaStreamTrack,
  ) => Promise<void> | void;
}) {
  const [name, setName] = useState(
    () => localStorage.getItem(NAME_KEY) ?? identity.name ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [archive, setArchive] = useState(false);
  const [guestLink, setGuestLink] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (name) localStorage.setItem(NAME_KEY, name);
  }, [name]);

  /*
   * Persist the stage name against the ACCOUNT, debounced.
   *
   * localStorage alone is per-device, which is the exact problem accounts were
   * meant to end — and without this the name Apple supplied wins on every other
   * device. Only for signed-in hosts; an anonymous session carries its name in
   * its own cookie.
   */
  useEffect(() => {
    if (!auth.isSignedIn() || !name.trim()) return;
    const timer = setTimeout(() => {
      void api.setStageName(name.trim()).catch(() => {
        /* Not worth interrupting the host over; it retries on the next edit. */
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [name]);

  const goLive = useCallback(async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    setError(null);
    haptic('press');
    try {
      /*
       * Permission FIRST, while this tap is still a live user gesture.
       *
       * Everything after here is awaits — a fetch, then a WebRTC connect — and
       * iOS stops counting the tap as a gesture across them. Asking afterwards
       * meant the prompt never appeared and the host went live silent, which is
       * the quietest possible failure: the room looks perfect and nobody can
       * hear you.
       */
      const mic = await primeMicrophone();
      if (!mic.ok) {
        haptic('snag');
        setError(mic.message);
        setBusy(false);
        return;
      }
      try {
        await onLive(await api.startTodaysShow(name.trim()), mic.track);
      } catch (caught) {
        // The room never opened, so nothing will publish this track — release
        // it or the phone keeps showing a recording indicator.
        mic.track.stop();
        throw caught;
      }
    } catch (caught) {
      haptic('snag');
      setError(caught instanceof Error ? caught.message : 'Could not start the show.');
      setBusy(false);
    }
  }, [busy, name, onLive]);

  /*
   * One link for a regular guest, minted on demand and safe to keep.
   *
   * The dated invites this replaces named a single room id, so a link sent on
   * Friday opened Friday's empty room on Saturday — it redeemed cleanly and put
   * the guest somewhere nobody was, which is why it went unreported for a day.
   */
  const makeGuestLink = useCallback(async () => {
    if (linkBusy) return;
    setLinkBusy(true);
    haptic('press');
    try {
      const { url } = await api.createStandingInvite('speaker');
      setGuestLink(url);
      // Clipboard can be refused (permissions, or no gesture left after the
      // await); the link is shown on screen either way, so this is a bonus.
      await navigator.clipboard?.writeText(url).catch(() => {});
    } catch (caught) {
      haptic('snag');
      setError(caught instanceof Error ? caught.message : 'Could not make a link.');
    } finally {
      setLinkBusy(false);
    }
  }, [linkBusy]);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  if (archive) return <Recordings onClose={() => setArchive(false)} />;

  return (
    <main class="host">
      <header class="host__head">
        <Avatar sub={identity.sub} name={name || identity.hostId || 'Host'} size="lg" />
        <div>
          <p class="host__hello">{name ? `Hello, ${name}` : 'Waves'}</p>
          <p class="t-foot">{today}</p>
        </div>
      </header>

      {/*
        Asked once, then never again — but editable, because the first thing
        anyone does with a new app is mistype their own name.
      */}
      <label class="host__field">
        <span class="cell__label">Your name on stage</span>
        <input
          type="text"
          value={name}
          placeholder="e.g. Kuro"
          autocomplete="nickname"
          onInput={(event) => setName((event.target as HTMLInputElement).value)}
        />
      </label>

      {error ? <p class="notice notice--bad">{error}</p> : null}

      <button class="host__go" onClick={goLive} disabled={busy || !name.trim()}>
        {busy ? 'Starting…' : 'Go live'}
      </button>

      <p class="host__hint">
        Opens today's show. Whoever taps first starts it — the other walks
        straight in.
      </p>

      {/*
        Secondary on purpose. Past shows matter, but not at 8pm with a room
        waiting — this stays a quiet link under the one button that counts.
      */}
      <button class="host__archive" onClick={() => void makeGuestLink()} disabled={linkBusy}>
        {linkBusy ? 'Making a link…' : 'Guest link'}
      </button>

      {guestLink ? (
        <div class="host__link">
          <p class="t-foot">
            Copied. Send this to a guest — it opens whichever show is running that
            day, so it keeps working.
          </p>
          <input class="host__linkbox" readOnly value={guestLink} onFocus={(e) => (e.target as HTMLInputElement).select()} />
        </div>
      ) : null}

      <button class="host__archive" onClick={() => setArchive(true)}>
        Past recordings
      </button>

      {/* Only when there is an account to sign out OF — an enrolled device has
          no session here to end, and the button would do nothing. */}
      {auth.isSignedIn() ? (
        <button
          class="host__archive"
          onClick={() => {
            void auth.signOut().then(() => window.location.assign('/host'));
          }}
        >
          Sign out
        </button>
      ) : null}
    </main>
  );
}
